import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import moment from "moment-timezone";
/**Components**/
import "react-js-cron/dist/styles.css";
import Modal from "../../utils/components/common/modal";

/**Utils**/
import { OrganisationContext } from "../../layouts/MainLayout";
import { getScheduleTemplates } from "../../services/SchedulerService";
import LoadingSpinner from "../../utils/components/common/loadingSpinner/LoadingSpinner";
import ModalComponent from "../../utils/components/common/modalComponent/ModalComponent";
import EmptyScreen from "../../utils/components/common/EmptyScreen/EmptyScreen";
import Icons from "../../utils/components/icons";

const LoadTemplate = ({ onLoadTemplateModalClose, currentSelectedTemplate }) => {
  const { siteData } = useContext(OrganisationContext);
  const searchRef = useRef(null)
  const { t } = useTranslation("schedules");
  const [isModalOpen, setIsModalOpen] = useState(true);
  const [err, setErr] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [templateList, setTemplateList] = useState([]);
  const [templateSearchedList, setTemplateSearchedList] = useState([]);
  const [totalTemplateCount, setTotalTemplateCount] = useState();
  const [selectedTemplateObj, setSelectedTemplateObj] = useState({});
  const [searchTerm, setSearchTerm] = useState("");

  // Search item from local list
  const searchedItemFun = useCallback(
    (term, itemsArr) => {
      if (!term.length) {
        setTemplateSearchedList([...itemsArr]) // Return the original list if the search term is empty
        setSelectedTemplateObj(itemsArr[0])
        return
      }

      // Filter the itemsArr based on the search term
      let res = itemsArr.filter((item) => {
        const name = item.scheduleTemplateName.toLowerCase();
        const lowerTerm = term.toLowerCase();
        return name.includes(lowerTerm);
      });
      setTemplateSearchedList([...res])
      setSelectedTemplateObj(res[0])
    }
    , []
  )

  // GetTemplate api method
  const getTemplateList = useCallback(
    async () => {
      setIsLoading(true);
      const localTimeZone = moment.tz.guess();
      const params = {
        siteId: siteData.id,
        sort: [{ "scheduleTemplateName": "asc" }],
        userTimezone: localTimeZone,
      };

      try {
        const response = await getScheduleTemplates(params);
        const item = response?.data?.Items
        setTemplateList(item || []);
        searchedItemFun(searchTerm, item)
        setSelectedTemplateObj(item.length > 0 ? item[0] : {})
        setTotalTemplateCount(response?.data?.OverallSchedulesCount);
        setIsLoading(false);
      } catch (error) {
        setIsLoading(false);
        setTemplateList([]);
        setErr(true)
      }
    },
    [siteData.id]
  );

  useEffect(() => {
    getTemplateList()
  }, [getTemplateList])

  // Close modal
  const onCloseClick = useCallback(
    () => {
      setIsModalOpen(false);
      onLoadTemplateModalClose(null);
    }, [onLoadTemplateModalClose]

  )

  // Load button click
  const onLoadClick = useCallback(
    () => {
      currentSelectedTemplate(selectedTemplateObj)
      onCloseClick()
    },
    [currentSelectedTemplate, onCloseClick, selectedTemplateObj]
  )

  // Close error modal
  const closeErrModal = useCallback(
    () => {
      setIsModalOpen(false);
      onLoadTemplateModalClose(null);
      setErr(false)
    }
    , [onLoadTemplateModalClose]
  )


  // Search term handle
  const handleSearchChange = useCallback(
    (e) => {
      const value = e.target.value
      setSearchTerm(value);
      searchedItemFun(value, templateList)
    }
    , [searchedItemFun, templateList]
  )

  // Clear search
  const clearSearch = useCallback(
    () => {
      setSearchTerm("");
      searchedItemFun("", templateList)
    }
    , [searchedItemFun, templateList]
  )

  // Search on Enter key
  const handleKeyDown = useCallback(
    (event) => {
      if (event.key === "Enter") {
        searchedItemFun(searchTerm, templateList)
      }
    }
    , [searchTerm, searchedItemFun, templateList]
  )

  // Focus on search input when click on magnify icon
  const focusForSearch = useCallback(
    () => {
      searchRef.current.focus();
    }
    , []
  )

  const isItems = templateSearchedList?.length > 0

  return (
    <div>
      {
        !err &&
        <Modal isOpen={isModalOpen} className="loadTemplateModal" id="select-template">
          {isLoading && <LoadingSpinner />}

          {/* Header Starts */}
          <div className="template-header">
            <p className="heading" id="select-temp-heading">{t("loadTemplate.selectTemplate")} ({searchTerm && `${templateSearchedList.length}/`}{totalTemplateCount || 0})</p>
            <div className="search-container">
              <input
                ref={searchRef}
                id="search_input"
                type="search"
                placeholder={t("loadTemplate.searchPlaceholder")}
                value={searchTerm}
                onChange={handleSearchChange}
                onKeyDown={handleKeyDown}
              />
              {
                searchTerm ? <span
                  id="xClearIcon"
                  className="xCircle"
                  onClick={clearSearch}
                  title={t("clear")}
                >
                  {Icons.xCircle}
                </span> :
                  <span
                    id="searchMagnifyIcon"
                    className={`magnify-icon magnify-icon-load-template ${templateList.length ? "" : "searchIconColor"}`}
                    onClick={focusForSearch}>
                    {Icons.magnify}
                  </span>
              }

            </div>
          </div>
          {/* Header Ends */}

          {/* Body Container Starts */}
          <div className="template-body-container" id="template-body-container">
            <div className="templateList">
              {
                isItems ? templateSearchedList?.map((item, idx) => {
                  const isTempActive = selectedTemplateObj?.scheduleTemplateName === item?.scheduleTemplateName
                  return <div
                    key={idx}
                    className={`cardItem ${isTempActive ? "changeBckColor" : ""}`}
                    id="card-item"
                    name="card-item"
                    onClick={() => setSelectedTemplateObj(templateSearchedList[idx])}
                  >
                    <input
                      id={`template${idx}`}
                      type="radio"
                      name="template"
                      className="radio-btn radio-btn-default"
                      value={item.scheduleTemplateName}
                      checked={isTempActive}
                    />
                    <span htmlFor={`template${idx}`} className="heading-h4 tempHeading" id="tempHeading" name="tempHeading">
                      {item?.scheduleTemplateName}
                    </span>
                    <span htmlFor={`template${idx}`} className={`text-h2 description ${isTempActive ? "showAllContent" : ""}`} id="tempDesc" name="tempDesc">
                      {item?.scheduleTemplateDescription}
                    </span>
                  </div>
                }) : !isLoading && <div className="empty-screen-container">
                  <EmptyScreen
                    small={true}
                    img={Icons.scheduleIcon}
                    title={templateList.length ? t("loadTemplate.noResultsfound") : t("loadTemplate.noTemplateFound")}
                    desc={templateList.length ? t("loadTemplate.unableToFindAMatch") : t("loadTemplate.addTemplate")}
                  />
                </div>
              }
            </div>
          </div>

          <div className="job-footer">
            <button id="closeBtn" className="roundBtn dark" onClick={onCloseClick}>
              <span>{t("loadTemplate.close")}</span>
            </button>
            <button
              id="loadBtn"
              className="roundBtn primary"
              onClick={onLoadClick}
              disabled={!isItems}
            >
              <span>{t("loadTemplate.load")}</span>
            </button>
          </div>
        </Modal>
      }

      {/* Err/Success Modal */}
      {err && <ModalComponent
        idName="template-info-modal"
        isOpen={err}
        onCloseModal={closeErrModal}
        descriptionText={t("loadTemplate.errMessage")}
      />}

    </div>
  );
};

export default LoadTemplate;
